import { z } from "zod";
import axiosInstance from "../../axiosInstance";
import { Invoice } from "../billingService/billingService";

export type IBillsPage = {
  user_id: number;
  all_invoices: number;
  count_of_debt_invoices: number;
  count_of_unpaid_invoices: number;
  invoices?: Invoice[];
  total_pages: number;
  current_page: number;
  total_status: boolean;
  total_amount_all: string;
};

export type ApartmentInvoices = {
  apartment_id: number;
  invoices: Invoice[];
};

export type IHouseManagerBillsPage = {
  user_id: number;
  all_invoices: number;
  count_of_debt_invoices: number;
  count_of_unpaid_invoices: number;
  apartments: ApartmentInvoices[];
  total_pages: number;
  current_page: number;
};

export type IAllAddresses = {
  user_id: number;
  addresses: UserAddess[];
};

export type IBuilding = {
  buildings?: {
    building_id: number;
    address: string;
    apartments: {
      apartment_id: number;
      apartment_number?: number;
      address: string;
    }[];
  }[];
  count: number;
};

export interface ResultMeters {
  address: string;
  apartment_number: string;
  period: string;
  meters: {
    meter_id: number;
    sensor_type: string;
    sensor_number: string;
    current_month_total_reading: string;
    previous_month_total_reading: string;
    total_reading_by_user: number | null;
    total_reading_last_updated: number | null;
  }[];
}

export type IMetersPage = {
  result: ResultMeters[];
  total_pages: number;
  current_page: number;
  all_apartments: number;
  not_received_readings_count: number | null;
};

export type HouseAdminDetails = {
  first_name: string;
  last_name: string;
  email?: string;
  phone_number: string;
};

export type EmergencyInfo = {
  id?: number;
  building?: number;
  house_admin_details?: HouseAdminDetails;
  access_codes?: string[];
  telegram_link?: string;
  whatsapp_link?: string;
  cumulative_fund?: string | null;
  service_company_debt?: string | null;
  utility_debt?: string | null;
  info?: string;
  contacts_info?: string;
};

export const houseAdminDetailsSchema = z.object({
  first_name: z.string().min(1),
  last_name: z.string().min(1),
  phone_number: z.number().min(1).max(15),
});

export type UserProfile = {
  first_name: string;
  last_name: string;
  phone_number: string;
  telegram?: string;
  whatsapp?: string;
  info?: string;
};

export interface INotices {
  id: number;
  title: string;
  description: string;
  created_at: string;
  is_emergency: boolean;
  building: {
    id: number;
    address: string;
  };
}

export interface INoticePost {
  title: string;
  description: string;
  building_id: number;
  is_emergency?: boolean;
}

export type UserAddess = {
  apartment_id: number;
  address: string;
  apartment_number: number;
  is_admin_only: boolean;
};

export type InvoiceBreakdown = {
  apartment_id: number;
  apartment_address: string;
  year: number;
  month: number;
  billing_period: string;
  invoices_count: number;
  breakdown: {
    electricity: string | null;
    water: string | null;
    heating: string | null;
    cold_water: string | null;
    hot_water: string | null;
    gas: string | null;
    management_fee: string | null;
    house_fund: string | null;
    waste: string | null;
    other: string | null;
    total: string | null;
  };
};

export type UtilityType =
  | "electricity"
  | "heating"
  | "cold_water"
  | "hot_water"
  | "gas"
  | "management_fee"
  | "house_fund"
  | "waste"
  | "other";

export interface UtilityData {
  year: number;
  data_type: "measurements" | "costs";
  utility_type: UtilityType;
  apartments_count: number;
  data: {
    apartment_id: number;
    apartment_address: string;
    monthly_data: {
      month: number;
      month_name: string;
      date: string;
      value: string;
    }[];
  }[];
}

const BillsService = {
  getBills: async <T = IBillsPage>(
    paid: true | false | undefined,
    address?: string,
    month?: number | string,
    year?: number | string,
    page?: number,
    page_size?: number,
    status?: "paid" | "unpaid" | "debt" | undefined,
    building?: number | string,
    apartment_number?: number | string,
    show_not_closed?: true | false | undefined,
    admin_view?: "all" | "personal" | null
  ): Promise<T> => {
    try {
      const response = await axiosInstance.get<T>(
        "/api/billing/get-user-invoices/",
        {
          params: {
            paid,
            address,
            month,
            year,
            page,
            page_size,
            status,
            admin_view,
            building,
            apartment_number,
            show_not_closed,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error while fetching bills:", error);
      throw error;
    }
  },

  postCancelPeriod: async (building_id: number): Promise<void> => {
    try {
      await axiosInstance.post("/api/billing/unclosed-periods/", {
        building_id,
      });
    } catch (error) {
      console.error("Error while canceling period:", error);
      throw error;
    }
  },

  getMeters: async (
    building_id: number | string,
    sensor_type: string | null,
    apartment_number?: number | string | null,
    page?: number,
    page_size?: number
  ): Promise<IMetersPage> => {
    try {
      const response = await axiosInstance.get<IMetersPage>(
        "/api/location/house-admin/meters/",
        {
          params: {
            building_id,
            sensor_type,
            apartment_number,
            page,
            page_size,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error while fetching meters:", error);
      throw error;
    }
  },

  getBuildings: async (): Promise<IBuilding> => {
    try {
      const response = await axiosInstance.get<IBuilding>(
        "/api/location/house-admin/buildings/"
      );
      return response.data;
    } catch (error) {
      console.error("Error while fetching buildings:", error);
      throw error;
    }
  },

  getEmergencyInfo: async (buildingId: number): Promise<EmergencyInfo> => {
    try {
      const response = await axiosInstance.get<EmergencyInfo>(
        `/api/location/buildings/${buildingId}/emergency-info/`
      );
      return response.data;
    } catch (error) {
      console.error("Error while fetching emergency info:", error);
      throw error;
    }
  },

  putEmergencyInfo: async (
    buildingId: number,
    data: EmergencyInfo
  ): Promise<EmergencyInfo> => {
    try {
      const response = await axiosInstance.put<EmergencyInfo>(
        `/api/location/buildings/${buildingId}/emergency-info/`,
        data
      );
      return response.data;
    } catch (error) {
      console.error("Error while fetching emergency info:", error);
      throw error;
    }
  },

  patchUserProfile: async (data: UserProfile): Promise<UserProfile> => {
    try {
      const response = await axiosInstance.patch<UserProfile>(
        "/api/user/profile/",
        data
      );
      return response.data;
    } catch (error) {
      console.error("Error while updating user profile:", error);
      throw error;
    }
  },

  getAddresses: async (): Promise<IAllAddresses> => {
    try {
      const response = await axiosInstance.get<IAllAddresses>(
        "/api/billing/get-user-addresses/"
      );

      return response.data;
    } catch (error) {
      console.error("Error while fetching addresses:", error);
      throw error;
    }
  },

  getNotices: async (building_id?: number): Promise<INotices[]> => {
    try {
      const response = await axiosInstance.get<INotices[]>(
        `/api/user/notices/`,
        { params: { building_id } }
      );
      return response.data;
    } catch (error) {
      console.error("Error while fetching notices:", error);
      throw error;
    }
  },

  postNotice: async (data: INoticePost): Promise<INoticePost> => {
    try {
      const response = await axiosInstance.post<INoticePost>(
        "/api/user/notices/",
        data
      );
      return response.data;
    } catch (error) {
      console.error("Error while posting notice:", error);
      throw error;
    }
  },

  patchNotice: async (id: number, data: INoticePost): Promise<INoticePost> => {
    try {
      const response = await axiosInstance.put<INoticePost>(
        `/api/user/notices/${id}/`,
        data
      );
      return response.data;
    } catch (error) {
      console.error("Error while patching notice:", error);
      throw error;
    }
  },

  deleteNotice: async (id: number): Promise<INotices> => {
    try {
      const response = await axiosInstance.delete<INotices>(
        `/api/user/notices/${id}/`
      );
      return response.data;
    } catch (error) {
      console.error("Error while deleting notice:", error);
      throw error;
    }
  },

  getInvoiceBreakdown: async (
    apartment_id: number | string,
    year: number | string,
    month: number | string
  ): Promise<InvoiceBreakdown> => {
    try {
      const response = await axiosInstance.get<InvoiceBreakdown>(
        `/api/billing/invoice-breakdown/${apartment_id}/`,
        {
          params: {
            year,
            month,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error while fetching invoice breakdown:", error);
      throw error;
    }
  },

  getUtilityData: async (
    year: string,
    apartment_id?: string,
    utility_type?: UtilityType,
    data_type?: "measurements" | "costs"
  ): Promise<UtilityData> => {
    try {
      const response = await axiosInstance.get<UtilityData>(
        `/api/billing/utility-data/`,
        {
          params: {
            year,
            apartment_id,
            utility_type,
            data_type,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error while fetching utility data:", error);
      throw error;
    }
  },
};

export default BillsService;
